import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import { LifeBuoy, Send, Mail, Phone, ShieldCheck, MessageSquareText, Headset } from "lucide-react";

const FAQS = [
  { q: "How do I know the other student is genuine?", a: "Every account is verified with an @srmist.edu.in email. You'll also see each student's star rating from previous deals before you chat or buy." },
  { q: "Where should I meet to hand off an item?", a: "Always meet at one of our predefined campus pickup points like Tech Park, UB Plaza or the Main Gate. These are high-traffic, monitored locations. Never meet off-campus." },
  { q: "What does the ₹29/month membership cover?", a: "The Campus Pass unlocks unlimited buying, selling and in-app chat, plus your verified trust badge. There is zero commission on your sales." },
  { q: "How are payments kept safe?", a: "We follow RBI digital payment norms. Cards are tokenized and never stored in raw form, and UPI uses the standard collect/intent flow." },
  { q: "What if there's a dispute with my deal?", a: "Message us here with your order ID. Our campus support team reviews disputes within 24 hours and can mediate between buyer and seller." },
];

export default function HelpCenter() {
  const { user } = useAuth();
  const { openAuth } = useOutletContext();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (user) api.get("/support").then(({ data }) => setMessages(data.messages)).catch(() => {});
  }, [user]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!user) return openAuth();
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/support", { text: text.trim() });
      setMessages(data.messages);
      setText("");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 lg:px-8 py-10 lg:py-14">
      <div className="max-w-2xl">
        <div className="inline-flex items-center gap-2 label-mono text-brand mb-2"><LifeBuoy size={15} /> Help Center</div>
        <h1 className="font-display font-extrabold text-ink text-3xl lg:text-4xl tracking-tight">We're here to help</h1>
        <p className="mt-3 text-slate-500 text-lg">Chat with support, or browse common questions from students.</p>
      </div>

      <div className="mt-10 grid lg:grid-cols-5 gap-6 items-start">
        {/* Support chat */}
        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col h-[520px]" data-testid="support-chat">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3 bg-slate-50">
            <div className="h-10 w-10 rounded-full bg-ink text-white grid place-items-center"><Headset size={18} /></div>
            <div>
              <div className="font-semibold text-ink text-sm flex items-center gap-1.5">SRM Marketplace Support <span className="h-2 w-2 rounded-full bg-emerald-500" /></div>
              <div className="text-xs text-slate-500">Typically replies in a few minutes</div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-3">
            {messages.length === 0 && (
              <div className="text-center text-sm text-slate-400 mt-16">
                <MessageSquareText size={30} className="mx-auto mb-3 text-slate-300" />
                {user ? "Start the conversation — ask about membership, payments, safety or verification." : "Log in to chat with our support team."}
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`} data-testid={`support-msg-${m.from}`}>
                <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  m.from === "user" ? "bg-brand text-white rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>

          <div className="p-3 border-t border-slate-100 flex gap-2">
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={user ? "Type your message…" : "Log in to chat"}
              className="h-11" data-testid="support-input"
            />
            <Button onClick={send} disabled={sending} className="h-11 px-4 bg-ink hover:bg-slate-800 text-white" data-testid="support-send-btn">
              <Send size={17} />
            </Button>
          </div>
        </div>

        {/* FAQ + contact */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h3 className="font-display font-bold text-lg text-ink mb-2">Frequently asked</h3>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-sm">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-slate-500 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <div className="bg-ink text-white rounded-2xl p-6">
            <div className="label-mono text-brand-100/70 mb-3 flex items-center gap-2"><ShieldCheck size={14} /> Contact us directly</div>
            <a href="mailto:rektr.srm@gmail.com" className="flex items-center gap-3 py-2 hover:text-brand-100" data-testid="contact-email">
              <Mail size={17} /> rektr.srm@gmail.com
            </a>
            <a href="tel:+918464050604" className="flex items-center gap-3 py-2 hover:text-brand-100" data-testid="contact-phone">
              <Phone size={17} /> +91 8464050604
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
